import {Signal} from "signals";
import {StopAutoplayCommand} from "slotworks/commands/autoplay/stop-autoplay";
import {SessionLimitCommand} from "slotworks/commands/error/session-limit-command";
import {HideAllLinesCommand} from "slotworks/commands/lines/hide-all-lines-command";
import {ShowLinesCommand} from "slotworks/commands/lines/show-lines-command";
import AutoplayOptions from "slotworks/components/betbar/vo/autoplay-options";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import GeneralOptions from "slotworks/components/betbar/vo/general-options";
import Components from "slotworks/components/components";
import Translations from "slotworks/i18n/translations";
import AbstractMediator from "slotworks/mediators/abstract-mediator";
import AutoplayModel from "slotworks/model/autoplay-model";
import BetModel from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import SettingsModel from "slotworks/model/settings/settings-model";
import SmartButton, {SmartButtonFlag} from "slotworks/smart-button/smart-button";
import Contract from "slotworks/utils/contract";
import {formatMinutes} from "slotworks/utils/string-formatters";
import Timer from "slotworks/utils/timer";
import WrapperMediator from "../integration/revolver/wrapper/wrapper-mediator";

export default class BetMenuMediator extends AbstractMediator {

    public onLinesChange: Signal;

    private initialized: boolean;

    private autoplayOptions: AutoplayOptions;
    private betOptions: BetOptions;
    private generalOptions: GeneralOptions;
    private sessionTimer: number;
    private hideLinesTimer: number;

    public start(): Contract<boolean> {
        this.initialized = true;

        this.autoplayOptions = AutoplayModel.generateAutoplayOptions();
        this.betOptions = BetModel.generateBetOptions();
        this.generalOptions = SettingsModel.generateGeneralOptions();
        Components.betBar.setEnabled(true);
        Components.betBar.setBetOptions(this.betOptions);
        Components.betBar.setHomeURL(SettingsModel.homeURL);

        Components.betMenu.setAutoplayOptions(this.autoplayOptions);
        Components.betMenu.setBetOptions(this.betOptions);
        Components.betMenu.setGeneralOptions(this.generalOptions);
        Components.betMenu.setHomeURL(SettingsModel.homeURL);

        this.update();

        this.handleSessionLimits();

        // Events
        this.onLinesChange = new Signal();

        Components.betMenu.onBetOptionsCommitted.add(this.betOptionsChangedHandler);
        Components.betMenu.onSettingsOptionsCommitted.add(this.generalOptionsChangedHandler);
        Components.betMenu.onAutoplayOptionsCommitted.add(this.autoplayOptionsChangedHandler);

        Components.betBar.onBetUpdated.add(this.betOptionsChangedHandler);

        return super.start();
    }

    public end() {
        Components.betMenu.hide();

        if (this.initialized) {
            this.initialized = false;

            this.clearListeners();
        }
    }

    private clearListeners() {
        Timer.clearTimeout(this.sessionTimer);

        this.onLinesChange.removeAll();
        Components.betMenu.onBetOptionsCommitted.remove(this.betOptionsChangedHandler);
        Components.betMenu.onSettingsOptionsCommitted.remove(this.generalOptionsChangedHandler);
        Components.betMenu.onAutoplayOptionsCommitted.remove(this.autoplayOptionsChangedHandler);

        Components.betBar.onBetUpdated.remove(this.betOptionsChangedHandler);

        Components.betBar.setEnabled(false);
    }

    private betOptionsChangedHandler = (newBetOptions: BetOptions) => {
        BetModel.setCoinSize(newBetOptions.coinSize, true);
        BetModel.setBetPerLine(newBetOptions.stakePerLine, true);
        BetModel.setSelectedLines(newBetOptions.lines, true);

        if (BetModel.getSelectedLines() !== newBetOptions.lines) {
            this.linesUpdated(newBetOptions.lines);
        } else {
            this.onLinesChange.dispatch();
        }

        this.update();
    }

    private generalOptionsChangedHandler = (newGeneralOptions: GeneralOptions) => {

        SettingsModel.updateFromOptions(newGeneralOptions);

        this.handleSessionLimits();

        this.update();
    }

    private autoplayOptionsChangedHandler = (newAutoplayOptions: AutoplayOptions) => {
        const autoplays = newAutoplayOptions.autoplays;

        AutoplayModel.updateFromOptions(newAutoplayOptions);

        SmartButton.setFlag(SmartButtonFlag.AUTO_SPIN_SELECT, autoplays > 0);
    }

    private linesUpdated = (selectedLines: number) => {
        this.betOptions.lines = selectedLines;
        BetModel.setSelectedLines(selectedLines);

        this.onLinesChange.dispatch();

        Timer.clearTimeout(this.hideLinesTimer);

        HideAllLinesCommand().execute();
        ShowLinesCommand(selectedLines).execute();

        this.hideLinesTimer = Timer.setTimeout(() => HideAllLinesCommand(), 1000);

        this.update();
    }

    private update() {
        WrapperMediator.balanceUpdated(GameState.getCurrentGame().balance);
        Components.betMenu.setBalance(GameState.getCurrentGame().balance);
        Components.betBar.setBalance(GameState.getCurrentGame().balance);

        Components.betBar.setCoinSize(this.betOptions.coinSize);
        Components.betBar.setLines(this.betOptions.lines);
        Components.betBar.setStake(this.betOptions.stakePerLine);
    }

    private handleSessionLimits() {
        const sessionLimit = SettingsModel.sessionLimit;
        const sessionTimeElapsed = SettingsModel.getSessionLength();

        if (this.sessionTimer) {
            Timer.clearTimeout(this.sessionTimer);
        }

        if (sessionLimit > 0) {
            if (sessionTimeElapsed >= sessionLimit) {
                this.showSessionLimitMessage(SettingsModel.sessionLimit);
            } else {
                Timer.clearTimeout(this.sessionTimer);
                this.sessionTimer = Timer.setTimeout(() => this.showSessionLimitMessage(SettingsModel.sessionLimit), (sessionLimit - sessionTimeElapsed) * 60000);
            }
        }
    }

    private showSessionLimitMessage(sessionLength: number) {
        const timeText = formatMinutes(sessionLength);
        StopAutoplayCommand();
        SessionLimitCommand(Translations.get("gen.sli", { duration: timeText, minutes: timeText }), SettingsModel.homeURL).then((goHome: boolean) => {
            if (!goHome) {
                SettingsModel.startSession();
                this.handleSessionLimits();
            }
        });
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/mediators/bet-menu-mediator.ts